import React, { useState, useMemo } from "react";
import { Collapse, Tag, Table, Row, Col, Statistic, Divider, Pagination, Input, Space, Typography, Select, Card, Button } from "antd";
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  SearchOutlined,
  FilterOutlined,
} from "@ant-design/icons";
import { BulkStatusDocResult, PODocumentResult } from "../../../interfaces/account.interface";

const { Text } = Typography;

type FilterType = "all" | "success" | "failed";

interface Props {
  data: BulkStatusDocResult;
}

const PAGE_SIZE = 20;

const DOC_LABEL_MAP: Record<string, string> = {
  "PO": "Purchase Order",
  "SO": "Sales Order",
  "TO": "Transfer Order",
  "GRN": "Goods Receipt Note",
  "Invoice": "Invoice",
  "EWayBill": "E-Way Bill",
  "Credits": "Apply Credits",
  "ServiceInvoice": "Service Invoice",
  "Package": "Package",
  "Shipment": "Shipment",
  "MarkDelivered": "Mark Delivered",
  "TO-Transferred": "Transfer Order (Transferred)",
  "TO-Revision": "Transfer Order (Revision)",
};

function parseDocField(raw: string): { leg: string | null; docName: string } {
  const legMatch = raw.match(/^Leg(\d+)-(.+)$/);
  if (legMatch) {
    return { leg: legMatch[1], docName: DOC_LABEL_MAP[legMatch[2]] || legMatch[2] };
  }
  return { leg: null, docName: DOC_LABEL_MAP[raw] || raw };
}

const docColumns = [
  {
    title: "Leg",
    key: "leg",
    width: 60,
    align: "center" as const,
    render: (_: any, record: any) => {
      const { leg } = parseDocField(record.doc);
      return leg ? <Tag color="blue">{leg}</Tag> : "—";
    },
  },
  {
    title: "Document",
    dataIndex: "doc",
    key: "doc",
    width: 200,
    render: (doc: string) => {
      const { docName } = parseDocField(doc);
      return <span style={{ fontWeight: 500 }}>{docName}</span>;
    },
  },
  {
    title: "Status",
    dataIndex: "success",
    key: "success",
    width: 110,
    render: (success: boolean) =>
      success ? (
        <Tag icon={<CheckCircleOutlined />} color="success">
          Success
        </Tag>
      ) : (
        <Tag icon={<CloseCircleOutlined />} color="error">
          Failed
        </Tag>
      ),
  },
  {
    title: "Message",
    dataIndex: "message",
    key: "message",
    render: (msg: string) => (
      <span style={{ color: "#595959", fontSize: 13 }}>{msg || "—"}</span>
    ),
  },
];

const ResultsPanel: React.FC<Props> = ({ data }) => {
  const [page, setPage] = useState(1);
  const [searchText, setSearchText] = useState("");
  const [filterType, setFilterType] = useState<FilterType>("all");
  const [selectedDocFilter, setSelectedDocFilter] = useState<string | null>(null);

  // Calculate document-level success counts (grouped by doc type, not per-leg)
  const documentStats = useMemo(() => {
    const stats: { [key: string]: { success: number; failed: number; total: number } } = {};

    data.results.forEach(po => {
      po.documents.forEach(doc => {
        const { docName } = parseDocField(doc.doc);
        if (!stats[docName]) {
          stats[docName] = { success: 0, failed: 0, total: 0 };
        }
        stats[docName].total++;
        if (doc.success) {
          stats[docName].success++;
        } else {
          stats[docName].failed++;
        }
      });
    });

    return stats;
  }, [data.results]);

  // Filter POs based on search and filter type
  const filteredResults = useMemo(() => {
    let filtered = data.results;

    // Apply document-specific filter (matches by parsed doc name across all legs)
    if (selectedDocFilter) {
      filtered = filtered.filter(po =>
        po.documents.some(doc => {
          const { docName } = parseDocField(doc.doc);
          return docName === selectedDocFilter && !doc.success;
        })
      );
    }
    // Apply status filter
    else if (filterType === "success") {
      filtered = filtered.filter(po => po.success);
    } else if (filterType === "failed") {
      filtered = filtered.filter(po => !po.success);
    }
    
    // Apply search filter
    if (searchText) {
      filtered = filtered.filter(po => 
        po.po_number.toLowerCase().includes(searchText.toLowerCase())
      );
    }
    
    return filtered;
  }, [data.results, searchText, filterType, selectedDocFilter]);

  const pageResults = filteredResults.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  // Calculate filtered stats
  const filteredStats = useMemo(() => {
    const total = filteredResults.length;
    const success = filteredResults.filter(po => po.success).length;
    const failed = filteredResults.filter(po => !po.success).length;
    return { total, success, failed };
  }, [filteredResults]);

  // Handler to filter by document type
  const handleDocFilter = (docType: string) => {
    if (selectedDocFilter === docType) {
      // Clear filter if clicking the same doc type
      setSelectedDocFilter(null);
      setFilterType("all");
    } else {
      // Set new document filter
      setSelectedDocFilter(docType);
      setFilterType("all"); // Reset general filter
      setPage(1); // Reset to first page
    }
  };

  const items = pageResults.map((r) => ({
    key: r.po_number,
    label: (
      <span>
        <span style={{ fontWeight: 600, marginRight: 10 }}>{r.po_number}</span>
        {r.success ? (
          <Tag icon={<CheckCircleOutlined />} color="success">
            Success
          </Tag>
        ) : (
          <Tag icon={<CloseCircleOutlined />} color="error">
            Failed
          </Tag>
        )}
      </span>
    ),
    children: (
      <Table<PODocumentResult>
        columns={docColumns}
        dataSource={[...r.documents]
          .sort((a, b) => {
            const legA = parseInt(parseDocField(a.doc).leg || "0");
            const legB = parseInt(parseDocField(b.doc).leg || "0");
            return legA - legB;
          })
          .map((d, i) => ({ ...d, key: i }))}
        pagination={false}
        size="small"
        bordered
      />
    ),
  }));

  return (
    <>
      {/* Remaining POs Info */}
      {data.remaining_po_count && data.remaining_po_count > 0 && (
        <Card style={{ marginBottom: 16, borderRadius: 8, backgroundColor: "#fff7e6", borderColor: "#ffd591" }}>
          <Row gutter={16} align="middle">
            <Col flex="auto">
              <Text strong style={{ color: "#d46b08" }}>
                {data.remaining_po_count} POs Remaining
              </Text>
              <br />
              <Text type="secondary" style={{ fontSize: 12 }}>
                {data.message || "These POs are still in the input field for processing in the next batch."}
              </Text>
            </Col>
            <Col>
              <Tag color="warning" style={{ fontSize: 12 }}>
                Click "Process Documents" again to continue
              </Tag>
            </Col>
          </Row>
        </Card>
      )}

      {/* Main Stats Card */}
      <Card style={{ marginBottom: 16, borderRadius: 8 }}>
        <Row gutter={32} align="middle">
          <Col>
            <Statistic 
              title="Total" 
              value={filteredStats.total} 
              suffix={`/ ${data.total}`}
            />
          </Col>
          <Col>
            <Statistic
              title="Success"
              value={filteredStats.success}
              valueStyle={{ color: "#52c41a" }}
              prefix={<CheckCircleOutlined />}
            />
          </Col>
          <Col>
            <Statistic
              title="Failed"
              value={filteredStats.failed}
              valueStyle={{ color: "#ff4d4f" }}
              prefix={<CloseCircleOutlined />}
            />
          </Col>
          <Col>
            <Statistic
              title="Success Rate"
              value={filteredStats.total > 0 ? Math.round((filteredStats.success / filteredStats.total) * 100) : 0}
              suffix="%"
              valueStyle={{ 
                color: filteredStats.success === filteredStats.total ? "#52c41a" : 
                       filteredStats.success === 0 ? "#ff4d4f" : "#faad14" 
              }}
            />
          </Col>
        </Row>
      </Card>

      {/* Document Success Stats */}
      {Object.keys(documentStats).length > 0 && (
        <Card style={{ marginBottom: 16, borderRadius: 8 }} title="Document Success Rates">
          <Space wrap>
            {Object.entries(documentStats).map(([doc, stats]) => {
              const isActive = selectedDocFilter === doc;
              const hasFailures = stats.failed > 0;
              
              return (
                <Tag 
                  key={doc} 
                  color={isActive ? "processing" : 
                          stats.success === stats.total ? "success" : 
                          stats.success === 0 ? "error" : "warning"}
                  style={{ 
                    marginBottom: 4, 
                    fontSize: 12,
                    cursor: hasFailures ? "pointer" : "default",
                    opacity: hasFailures ? 1 : 0.7,
                    border: isActive ? "2px solid #1890ff" : undefined
                  }}
                  onClick={() => hasFailures && handleDocFilter(doc)}
                >
                  {doc}: {stats.success}/{stats.total} ({Math.round((stats.success / stats.total) * 100)}%)
                  {hasFailures && (
                    <span style={{ marginLeft: 4 }}>
                      {isActive ? "✓" : "🔍"}
                    </span>
                  )}
                </Tag>
              );
            })}
          </Space>
          {selectedDocFilter && (
            <div style={{ marginTop: 8, fontSize: 12, color: "#666" }}>
              Showing POs with failed <strong>{selectedDocFilter}</strong> documents. 
              <Button 
                type="link" 
                size="small" 
                onClick={() => handleDocFilter(selectedDocFilter)}
                style={{ padding: 0, height: "auto" }}
              >
                Clear filter
              </Button>
            </div>
          )}
        </Card>
      )}

      {/* Filters Row */}
      <Row gutter={16} style={{ marginBottom: 16 }} align="middle">
        <Col flex="auto">
          <Space>
            <Text strong>Filters:</Text>
            <Select
              value={filterType}
              onChange={setFilterType}
              style={{ width: 120 }}
              suffixIcon={<FilterOutlined />}
            >
              <Select.Option value="all">All POs</Select.Option>
              <Select.Option value="success">Success Only</Select.Option>
              <Select.Option value="failed">Failed Only</Select.Option>
            </Select>
          </Space>
        </Col>
        <Col>
          <Input
            placeholder="Search PO numbers..."
            prefix={<SearchOutlined />}
            value={searchText}
            onChange={(e) => {
              setSearchText(e.target.value);
              setPage(1); // Reset to first page when searching
            }}
            style={{ width: 250 }}
            allowClear
          />
        </Col>
      </Row>

      {/* Results */}
      <Collapse
        items={items}
        defaultActiveKey={pageResults.filter((r) => !r.success).map((r) => r.po_number)}
      />

      {filteredResults.length > PAGE_SIZE && (
        <div style={{ marginTop: 16, textAlign: "right" }}>
          <Pagination
            current={page}
            pageSize={PAGE_SIZE}
            total={filteredResults.length}
            onChange={(p) => setPage(p)}
            showSizeChanger={false}
            showTotal={(total) => `${total} POs`}
          />
        </div>
      )}
    </>
  );
};

export default ResultsPanel;
